import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Lock } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

interface RowProps {
	/** Column key; also the sortable id and the checkbox/label pairing id. */
	columnKey: string;
	/** Already-translated column label. */
	label: string;
	checked: boolean;
	/** Locked-on column: the checkbox is shown checked and disabled. */
	locked?: boolean;
	onToggle: () => void;
}

interface ShellProps extends Omit<RowProps, "columnKey"> {
	id: string;
	/** The leading affordance: a drag grip, or a lock for the pinned column. */
	leading: React.ReactNode;
	dragging?: boolean;
	style?: React.CSSProperties;
	innerRef?: (node: HTMLElement | null) => void;
}

/** The shared row layout: leading affordance, checkbox, label. */
const ColumnRow: React.FC<ShellProps> = ({
	id,
	label,
	checked,
	locked,
	onToggle,
	leading,
	dragging,
	style,
	innerRef,
}) => (
	<div
		ref={innerRef}
		style={style}
		className={cn(
			"flex h-8 items-center gap-2 rounded-md px-1",
			dragging ? "relative z-10 bg-white shadow-md" : "hover:bg-accent"
		)}
	>
		{leading}
		<Checkbox
			id={`column-${id}`}
			checked={checked}
			disabled={locked}
			onCheckedChange={() => onToggle()}
		/>
		<label
			htmlFor={`column-${id}`}
			className={cn(
				"flex-1 cursor-pointer truncate text-sm",
				locked && "cursor-default",
				!checked && "text-muted-foreground"
			)}
		>
			{label}
		</label>
	</div>
);

/**
 * One draggable column row in the column-settings panel: grip, visibility
 * checkbox and label. The grip is the only drag handle (so the label stays a
 * plain click target for toggling), and hidden columns are dimmed but keep their
 * position in the list — showing one again puts it back where the user left it.
 */
const SortableColumnRow: React.FC<RowProps> = ({ columnKey, label, checked, locked, onToggle }) => {
	const { t } = useTranslation();
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: columnKey,
	});

	return (
		<ColumnRow
			id={columnKey}
			label={label}
			checked={checked}
			locked={locked}
			onToggle={onToggle}
			innerRef={setNodeRef}
			dragging={isDragging}
			style={{ transform: CSS.Transform.toString(transform), transition }}
			leading={
				<button
					type="button"
					aria-label={`${t("COLUMNS_MOVE")}: ${label}`}
					className={cn(
						"flex h-6 w-4 shrink-0 cursor-grab items-center justify-center text-muted-foreground",
						"hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action",
						isDragging && "cursor-grabbing"
					)}
					{...attributes}
					{...listeners}
				>
					<GripVertical className="h-4 w-4" />
				</button>
			}
		/>
	);
};

/**
 * The pinned first column (Namn / Anst.nr / Ordernr): the row anchor, always
 * visible and always leftmost, so it shows a lock instead of a grip and is
 * rendered outside the sortable context — it can neither be dragged nor be a
 * drop target.
 */
export const PinnedColumnRow: React.FC<{ columnKey: string; label: string }> = ({
	columnKey,
	label,
}) => {
	const { t } = useTranslation();

	return (
		<ColumnRow
			id={columnKey}
			label={label}
			checked
			locked
			onToggle={() => undefined}
			leading={
				<span
					title={t("COLUMNS_PINNED_HINT")}
					className="flex h-6 w-4 shrink-0 items-center justify-center text-muted-foreground"
				>
					<Lock className="h-3.5 w-3.5" />
				</span>
			}
		/>
	);
};

export default SortableColumnRow;
